<template>
    <div>
        <div class="row">
            <div class="col-6 col-md-8">
                <h4 class="group-name">
                    {{ name }}
                </h4>
            </div>
            <div class="col-6 col-md-4" :class="{'disabled': isCampCostEditingDisabled && !newPricing }">
                <choice option-width="97px"
                        :options="PAY_TYPES"
                        :value="free ? PAY_TYPES[0] : PAY_TYPES[1]"
                        @input="onFreeSelect">
                </choice>
            </div>
        </div>
        <div class="row">
            <div class="col-6" :class="{'disabled': isCampCostEditingDisabled && !newPricing}">
                <div class="price-wrapper form-group" >
                    <div class="d-flex align-items-center">
                        <label>Fee</label>
                        <div class="with-tooltip fee">
                            <img class="info-tooltip" src="../../../../assets/img/question.svg">
                            <div class="tooltiptext">
                                Enter the total amount parent will pay per billing cycle
                            </div>
                        </div>
                    </div>
                    <money maxlength="15"
                           :name="'fee' + pricing.id"
                           :disabled="free"
                           v-bind="money"
                           v-validate="{required: true, bigger: free ? false : 0}"
                           v-model="price">
                    </money>
                    <div class="error">{{ errors.first('fee' + pricing.id) }}</div>
                </div>
            </div>
            <div class="col-6" :class="{'disabled': isCampCostEditingDisabled && !newPricing}"> 
                <select-box label="Billing cycle"
                            class="billing"
                            info="<div style='text-align: left'>Select the way parents should be billed<br>
                                      Per Session - one time upfront fee for the session<br>
                                      Per Week - charge parent every week the class meets<br>
                                      Per Monthly - charge parents for camps in that month</div>"
                            :default-value="billingType"
                            :data-list="BILLING_PAYMENT_TYPES"
                            :format-callback="item => item.view"
                            :disabled="free"
                            @selected="billingType = $event">
                </select-box>
            </div>
        </div>
        <div v-if="showMobileVersion"
             class="row">
           <div class="col-6 offset-6 mb-20">
               <number-peel label="Max Enroll"
                            class="mb-0"
                            name="max-enroll"
                            v-model="maxEnroll">
               </number-peel>
           </div>
        </div>
        <div class="row">
            <div class="col-6 col-sm-4 is-waitlist">
                <switch-button v-bind:checked.sync="waitList"
                               label="Wait list">
                </switch-button>
            </div>
            <div v-if="!showMobileVersion"
                 class="col-6 col-sm-4">
                <number-peel label="Max Enroll"
                             class="mb-0"
                             name="max-enroll"
                             v-model="maxEnroll">
                </number-peel>
            </div>
            <div class="col-6 col-sm-4">
                <number-peel label="Wait list max"
                             class="mb-0"
                             name="wait-list-max"
                             :min-value="1"
                             :disabled="!waitList"
                             :initial-value="maxWaitList"
                             v-model="maxWaitList">
                </number-peel>
            </div>
        </div>
    </div>
</template>

<script>
    import * as _ from 'lodash';
    import { Money } from 'v-money';
    import { PriceMaskMixin } from '@/mixins/PriceMaskMixin';
    import { ResizeMobileMixin } from '@/mixins/ResizeMobileMixin';
    import { TYPES } from '@/constants/ages-grades';
    import { BILLING_PAYMENT_TYPES } from '@/constants/billing-payment-type';
    import { mapGetters } from 'vuex';

    const PAY_TYPES = ['Free', 'Paid'];

    export default {
        name: 'CampPricingEnd',
        components: {
            AppInput: () => import('@/components/controls/AppInput'),
            SwitchButton: () => import('@/components/controls/SwitchButton'),
            NumberPeel: () => import('@/components/controls/NumberPeel'),
            Choice: () => import('@/components/controls/Choice'),
            SelectBox: () => import('@/components/controls/SelectBox'),
            Money
        },
        inject: ['$validator'],
        props: {
            pricing: {
                type: Object,
                required: true,
            },
            newPricing: {
                type: Boolean,
                default: false,
            }
        },
        mixins: [PriceMaskMixin, ResizeMobileMixin(568)],
        data() {
            return {
                TYPES,
                BILLING_PAYMENT_TYPES,
                PAY_TYPES,
                free: false,
            }
        },
        computed: {
            ...mapGetters({
                isCampCostEditingDisabled: 'isCampCostEditingDisabled',
            }),
            name: {
                get() {
                    return this.pricing.name;
                },
                set(value) {
                    this.$emit('input:name', value);
                }
            },
            price: {
                get() {
                    return this.pricing.price;
                },
                set(value) {
                    this.$emit('input:price', value);
                }
            },
            waitList: {
                get() {
                    return this.pricing.waitList;
                },
                set(value) {
                    this.$emit('input:wait-list', value);
                }
            },
            maxWaitList: {
                get() {
                    return this.pricing.maxWaitList;
                },
                set(value) {
                    this.$emit('input:max-wait-list', value);
                }
            },
            maxEnroll: {
                get() {
                    return this.pricing.maxEnroll;
                },
                set(value) {
                    this.$emit('input:max-enroll', value);
                }
            },
            billingType: {
                get() {
                    return this.pricing.billingType && BILLING_PAYMENT_TYPES.find(item => this.pricing.billingType === item.data);
                },
                set(value) {
                    this.$emit('input:billing-type', value && value.data);
                }
            }

        },
        watch: {
            free: {
                handler(value) {
                    if (value) {
                        this.price = 0;
                    }
                },
                immediate: true
            }
        },
        methods: {
            formatBillingType(billingType) {
                return 'Per ' + _.capitalize(billingType);
            },
            onFreeSelect(data) {
                this.free = (data === PAY_TYPES[0]);
                // Emit paid flag: pricing is paid if not free
                this.$emit('input:paid', !this.free);
            },
        },
        created() {
            if (this.price === 0) {
                this.free = true;
            }
            this.$emit('input:paid', !this.free);
        }
    }
</script>

<style lang="scss" scoped>
    .group-name {
        word-wrap: break-word;
    }
    .mb-20 {
        margin-bottom: 20px;
    }
    .fee {
        .tooltiptext {
            @media screen and (max-width: 568px) {
                left: -40px;
            }
        }
    }
    .disabled {
            pointer-events: none;
            opacity: 0.5;
        }

    .billing {
        /deep/ .with-tooltip .tooltiptext {
            @media screen and (max-width: 991px) {
                right: -30px;
                left: auto;
                top: -100px;
            }
            @media screen and (max-width: 767px) {
                right: -60px;
                left: auto;
                top: 100%;
                max-width: 340px;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/camp-form/CampPricingEnd.vue